/**
 * This code was GENERATED using the solita package.
 * Please DO NOT EDIT THIS FILE, instead rerun solita to update it or write a wrapper to add functionality.
 *
 * See: https://github.com/metaplex-foundation/solita
 */

import * as web3 from '@solana/web3.js'
import * as beet from '@metaplex-foundation/beet'
import * as beetSolana from '@metaplex-foundation/beet-solana'

/**
 * Arguments used to create {@link MintManager}
 * @category Accounts
 * @category generated
 */
export type MintManagerArgs = {
  accountType: number[] /* size: 8 */
  version: number
  mint: web3.PublicKey
  authority: web3.PublicKey
  ruleset: web3.PublicKey
  inUseBy: beet.COption<web3.PublicKey>
}
/**
 * Holds the data for the {@link MintManager} Account and provides de/serialization
 * functionality for that data
 *
 * @category Accounts
 * @category generated
 */
export class MintManager implements MintManagerArgs {
  private constructor(
    readonly accountType: number[] /* size: 8 */,
    readonly version: number,
    readonly mint: web3.PublicKey,
    readonly authority: web3.PublicKey,
    readonly ruleset: web3.PublicKey,
    readonly inUseBy: beet.COption<web3.PublicKey>
  ) {}

  /**
   * Creates a {@link MintManager} instance from the provided args.
   */
  static fromArgs(args: MintManagerArgs) {
    return new MintManager(
      args.accountType,
      args.version,
      args.mint,
      args.authority,
      args.ruleset,
      args.inUseBy
    )
  }

  /**
   * Deserializes the {@link MintManager} from the data of the provided {@link web3.AccountInfo}.
   * @returns a tuple of the account data and the offset up to which the buffer was read to obtain it.
   */
  static fromAccountInfo(
    accountInfo: web3.AccountInfo<Buffer>,
    offset = 0
  ): [MintManager, number] {
    return MintManager.deserialize(accountInfo.data, offset)
  }

  /**
   * Retrieves the account info from the provided address and deserializes
   * the {@link MintManager} from its data.
   *
   * @throws Error if no account info is found at the address or if deserialization fails
   */
  static async fromAccountAddress(
    connection: web3.Connection,
    address: web3.PublicKey
  ): Promise<MintManager> {
    const accountInfo = await connection.getAccountInfo(address)
    if (accountInfo == null) {
      throw new Error(`Unable to find MintManager account at ${address}`)
    }
    return MintManager.fromAccountInfo(accountInfo, 0)[0]
  }

  /**
   * Provides a {@link web3.Connection.getProgramAccounts} config builder,
   * to fetch accounts matching filters that can be specified via that builder.
   *
   * @param programId - the program that owns the accounts we are filtering
   */
  static gpaBuilder(
    programId: web3.PublicKey = new web3.PublicKey(
      'ccsxqYAg64wuLEh45KabyPvkKjrEfDPsDZUQrGn7mf3'
    )
  ) {
    return beetSolana.GpaBuilder.fromStruct(programId, mintManagerBeet)
  }

  /**
   * Deserializes the {@link MintManager} from the provided data Buffer.
   * @returns a tuple of the account data and the offset up to which the buffer was read to obtain it.
   */
  static deserialize(buf: Buffer, offset = 0): [MintManager, number] {
    return mintManagerBeet.deserialize(buf, offset)
  }

  /**
   * Serializes the {@link MintManager} into a Buffer.
   * @returns a tuple of the created Buffer and the offset up to which the buffer was written to store it.
   */
  serialize(): [Buffer, number] {
    return mintManagerBeet.serialize(this)
  }

  /**
   * Returns the byteSize of a {@link Buffer} holding the serialized data of
   * {@link MintManager} for the provided args.
   *
   * @param args need to be provided since the byte size for this account
   * depends on them
   */
  static byteSize(args: MintManagerArgs) {
    const instance = MintManager.fromArgs(args)
    return mintManagerBeet.toFixedFromValue(instance).byteSize
  }

  /**
   * Fetches the minimum balance needed to exempt an account holding
   * {@link MintManager} data from rent
   *
   * @param args need to be provided since the byte size for this account
   * depends on them
   * @param connection used to retrieve the rent exemption information
   */
  static async getMinimumBalanceForRentExemption(
    args: MintManagerArgs,
    connection: web3.Connection,
    commitment?: web3.Commitment
  ): Promise<number> {
    return connection.getMinimumBalanceForRentExemption(
      MintManager.byteSize(args),
      commitment
    )
  }

  /**
   * Returns a readable version of {@link MintManager} properties
   * and can be used to convert to JSON and/or logging
   */
  pretty() {
    return {
      accountType: this.accountType,
      version: this.version,
      mint: this.mint.toBase58(),
      authority: this.authority.toBase58(),
      ruleset: this.ruleset.toBase58(),
      inUseBy: this.inUseBy,
    }
  }
}

/**
 * @category Accounts
 * @category generated
 */
export const mintManagerBeet = new beet.FixableBeetStruct<
  MintManager,
  MintManagerArgs
>(
  [
    ['accountType', beet.uniformFixedSizeArray(beet.u8, 8)],
    ['version', beet.u8],
    ['mint', beetSolana.publicKey],
    ['authority', beetSolana.publicKey],
    ['ruleset', beetSolana.publicKey],
    ['inUseBy', beet.coption(beetSolana.publicKey)],
  ],
  MintManager.fromArgs,
  'MintManager'
)
